import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  Paper,
} from '@mui/material';
import { 
  Save, 
  ExpandMore, 
  Visibility, 
  VisibilityOff,
  SmartToy,
  Person,
  Settings as SettingsIcon,
  Schedule,
  Public,
  Add,
  Delete,
  Contacts
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useGetCurrentUserQuery, useUpdateUserMutation } from '../store/api/userApi';
import { 
  useGetEmailCredentialsQuery, 
  useCreateEmailCredentialsMutation,
  useUpdateEmailCredentialsMutation,
  useDeleteEmailCredentialsMutation,
  UserEmailCredentials
} from '../store/api/userEmailCredentialsApi';
import { 
  useGetEmailAddressesQuery, 
  useCreateEmailAddressMutation,
  useUpdateEmailAddressMutation,
  useDeleteEmailAddressMutation,
  EmailAddress,
  EmailAddressDropdown
} from '../store/api/emailAddressesApi';

// Comprehensive timezone options
const timezoneOptions = [
  { value: 'UTC', label: 'UTC' },
  { value: 'US/Alaska', label: 'US/Alaska' },
  { value: 'US/Central', label: 'US/Central' },
  { value: 'US/Eastern', label: 'US/Eastern' },
  { value: 'US/Hawaii', label: 'US/Hawaii' },
  { value: 'US/Mountain', label: 'US/Mountain' },
  { value: 'US/Pacific', label: 'US/Pacific' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam' },
  { value: 'Europe/Andorra', label: 'Europe/Andorra' },
  { value: 'Europe/Astrakhan', label: 'Europe/Astrakhan' },
  { value: 'Europe/Athens', label: 'Europe/Athens' },
  { value: 'Europe/Belgrade', label: 'Europe/Belgrade' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin' },
  { value: 'Europe/Bratislava', label: 'Europe/Bratislava' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels' },
  { value: 'Europe/Bucharest', label: 'Europe/Bucharest' },
  { value: 'Europe/Budapest', label: 'Europe/Budapest' },
  { value: 'Europe/Busingen', label: 'Europe/Busingen' },
  { value: 'Europe/Chisinau', label: 'Europe/Chisinau' },
  { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen' },
  { value: 'Europe/Dublin', label: 'Europe/Dublin' },
  { value: 'Europe/Gibraltar', label: 'Europe/Gibraltar' },
  { value: 'Europe/Guernsey', label: 'Europe/Guernsey' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki' },
  { value: 'Europe/Isle_of_Man', label: 'Europe/Isle_of_Man' },
  { value: 'Europe/Istanbul', label: 'Europe/Istanbul' },
  { value: 'Europe/Jersey', label: 'Europe/Jersey' },
  { value: 'Europe/Kaliningrad', label: 'Europe/Kaliningrad' },
  { value: 'Europe/Kirov', label: 'Europe/Kirov' },
  { value: 'Europe/Kyiv', label: 'Europe/Kyiv' },
  { value: 'Europe/Lisbon', label: 'Europe/Lisbon' },
  { value: 'Europe/Ljubljana', label: 'Europe/Ljubljana' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'Europe/Luxembourg', label: 'Europe/Luxembourg' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid' },
  { value: 'Europe/Malta', label: 'Europe/Malta' },
  { value: 'Europe/Mariehamn', label: 'Europe/Mariehamn' },
  { value: 'Europe/Minsk', label: 'Europe/Minsk' },
  { value: 'Europe/Monaco', label: 'Europe/Monaco' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow' },
  { value: 'Europe/Oslo', label: 'Europe/Oslo' },
  { value: 'Europe/Paris', label: 'Europe/Paris' },
  { value: 'Europe/Podgorica', label: 'Europe/Podgorica' },
  { value: 'Europe/Prague', label: 'Europe/Prague' },
  { value: 'Europe/Riga', label: 'Europe/Riga' },
  { value: 'Europe/Rome', label: 'Europe/Rome' },
  { value: 'Europe/Samara', label: 'Europe/Samara' },
  { value: 'Europe/San_Marino', label: 'Europe/San_Marino' },
  { value: 'Europe/Sarajevo', label: 'Europe/Sarajevo' },
  { value: 'Europe/Saratov', label: 'Europe/Saratov' },
  { value: 'Europe/Simferopol', label: 'Europe/Simferopol' },
  { value: 'Europe/Skopje', label: 'Europe/Skopje' },
  { value: 'Europe/Sofia', label: 'Europe/Sofia' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm' },
  { value: 'Europe/Tallinn', label: 'Europe/Tallinn' },
  { value: 'Europe/Tirane', label: 'Europe/Tirane' },
  { value: 'Europe/Ulyanovsk', label: 'Europe/Ulyanovsk' },
  { value: 'Europe/Vaduz', label: 'Europe/Vaduz' },
  { value: 'Europe/Vatican', label: 'Europe/Vatican' },
  { value: 'Europe/Vienna', label: 'Europe/Vienna' },
  { value: 'Europe/Vilnius', label: 'Europe/Vilnius' },
  { value: 'Europe/Volgograd', label: 'Europe/Volgograd' },
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw' },
  { value: 'Europe/Zagreb', label: 'Europe/Zagreb' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich' },
  { value: 'Australia/Adelaide', label: 'Australia/Adelaide' },
  { value: 'Australia/Brisbane', label: 'Australia/Brisbane' },
  { value: 'Australia/Broken_Hill', label: 'Australia/Broken_Hill' },
  { value: 'Australia/Darwin', label: 'Australia/Darwin' },
  { value: 'Australia/Eucla', label: 'Australia/Eucla' },
  { value: 'Australia/Hobart', label: 'Australia/Hobart' },
  { value: 'Australia/Lindeman', label: 'Australia/Lindeman' },
  { value: 'Australia/Lord_Howe', label: 'Australia/Lord_Howe' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne' },
  { value: 'Australia/Perth', label: 'Australia/Perth' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney' },
  { value: 'Africa/Accra', label: 'Africa/Accra' },
  { value: 'Africa/Addis_Ababa', label: 'Africa/Addis_Ababa' },
  { value: 'Africa/Algiers', label: 'Africa/Algiers' },
  { value: 'Africa/Asmara', label: 'Africa/Asmara' },
  { value: 'Africa/Bamako', label: 'Africa/Bamako' },
  { value: 'Africa/Bangui', label: 'Africa/Bangui' },
  { value: 'Africa/Banjul', label: 'Africa/Banjul' },
  { value: 'Africa/Bissau', label: 'Africa/Bissau' },
  { value: 'Africa/Blantyre', label: 'Africa/Blantyre' },
  { value: 'Africa/Brazzaville', label: 'Africa/Brazzaville' },
  { value: 'Africa/Bujumbura', label: 'Africa/Bujumbura' },
  { value: 'Africa/Cairo', label: 'Africa/Cairo' },
  { value: 'Africa/Casablanca', label: 'Africa/Casablanca' },
  { value: 'Africa/Ceuta', label: 'Africa/Ceuta' },
  { value: 'Africa/Conakry', label: 'Africa/Conakry' },
  { value: 'Africa/Dakar', label: 'Africa/Dakar' },
  { value: 'Africa/Dar_es_Salaam', label: 'Africa/Dar_es_Salaam' },
  { value: 'Africa/Djibouti', label: 'Africa/Djibouti' },
  { value: 'Africa/Douala', label: 'Africa/Douala' },
  { value: 'Africa/El_Aaiun', label: 'Africa/El_Aaiun' },
  { value: 'Africa/Freetown', label: 'Africa/Freetown' },
  { value: 'Africa/Gaborone', label: 'Africa/Gaborone' },
  { value: 'Africa/Harare', label: 'Africa/Harare' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg' },
  { value: 'Africa/Juba', label: 'Africa/Juba' },
  { value: 'Africa/Kampala', label: 'Africa/Kampala' },
  { value: 'Africa/Khartoum', label: 'Africa/Khartoum' },
  { value: 'Africa/Kigali', label: 'Africa/Kigali' },
  { value: 'Africa/Kinshasa', label: 'Africa/Kinshasa' },
  { value: 'Africa/Lagos', label: 'Africa/Lagos' },
  { value: 'Africa/Libreville', label: 'Africa/Libreville' },
  { value: 'Africa/Lome', label: 'Africa/Lome' },
  { value: 'Africa/Luanda', label: 'Africa/Luanda' },
  { value: 'Africa/Lubumbashi', label: 'Africa/Lubumbashi' },
  { value: 'Africa/Lusaka', label: 'Africa/Lusaka' },
  { value: 'Africa/Malabo', label: 'Africa/Malabo' },
  { value: 'Africa/Maputo', label: 'Africa/Maputo' },
  { value: 'Africa/Maseru', label: 'Africa/Maseru' },
  { value: 'Africa/Mbabane', label: 'Africa/Mbabane' },
  { value: 'Africa/Mogadishu', label: 'Africa/Mogadishu' },
  { value: 'Africa/Monrovia', label: 'Africa/Monrovia' },
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi' },
  { value: 'Africa/Ndjamena', label: 'Africa/Ndjamena' },
  { value: 'Africa/Niamey', label: 'Africa/Niamey' },
  { value: 'Africa/Nouakchott', label: 'Africa/Nouakchott' },
  { value: 'Africa/Ouagadougou', label: 'Africa/Ouagadougou' },
  { value: 'Africa/Porto-Novo', label: 'Africa/Porto-Novo' },
  { value: 'Africa/Sao_Tome', label: 'Africa/Sao_Tome' },
  { value: 'Africa/Tripoli', label: 'Africa/Tripoli' },
  { value: 'Africa/Tunis', label: 'Africa/Tunis' },
  { value: 'Africa/Windhoek', label: 'Africa/Windhoek' },
  { value: 'America/Adak', label: 'America/Adak' },
  { value: 'America/Anchorage', label: 'America/Anchorage' },
  { value: 'America/Anguilla', label: 'America/Anguilla' },
  { value: 'America/Antigua', label: 'America/Antigua' },
  { value: 'America/Araguaina', label: 'America/Araguaina' },
  { value: 'America/Argentina/Buenos_Aires', label: 'America/Argentina/Buenos_Aires' },
  { value: 'America/Argentina/Catamarca', label: 'America/Argentina/Catamarca' },
  { value: 'America/Argentina/Cordoba', label: 'America/Argentina/Cordoba' },
  { value: 'America/Argentina/Jujuy', label: 'America/Argentina/Jujuy' },
  { value: 'America/Argentina/La_Rioja', label: 'America/Argentina/La_Rioja' },
  { value: 'America/Argentina/Mendoza', label: 'America/Argentina/Mendoza' },
  { value: 'America/Argentina/Rio_Gallegos', label: 'America/Argentina/Rio_Gallegos' },
  { value: 'America/Argentina/Salta', label: 'America/Argentina/Salta' },
  { value: 'America/Argentina/San_Juan', label: 'America/Argentina/San_Juan' },
  { value: 'America/Argentina/San_Luis', label: 'America/Argentina/San_Luis' },
  { value: 'America/Argentina/Tucuman', label: 'America/Argentina/Tucuman' },
  { value: 'America/Argentina/Ushuaia', label: 'America/Argentina/Ushuaia' },
  { value: 'America/Aruba', label: 'America/Aruba' },
  { value: 'America/Asuncion', label: 'America/Asuncion' },
  { value: 'America/Atikokan', label: 'America/Atikokan' },
  { value: 'America/Bahia', label: 'America/Bahia' },
  { value: 'America/Bahia_Banderas', label: 'America/Bahia_Banderas' },
  { value: 'America/Barbados', label: 'America/Barbados' },
  { value: 'America/Belem', label: 'America/Belem' },
  { value: 'America/Belize', label: 'America/Belize' },
  { value: 'America/Blanc-Sablon', label: 'America/Blanc-Sablon' },
  { value: 'America/Boa_Vista', label: 'America/Boa_Vista' },
  { value: 'America/Bogota', label: 'America/Bogota' },
  { value: 'America/Boise', label: 'America/Boise' },
  { value: 'America/Cambridge_Bay', label: 'America/Cambridge_Bay' },
  { value: 'America/Campo_Grande', label: 'America/Campo_Grande' },
  { value: 'America/Cancun', label: 'America/Cancun' },
  { value: 'America/Caracas', label: 'America/Caracas' },
  { value: 'America/Cayenne', label: 'America/Cayenne' },
  { value: 'America/Cayman', label: 'America/Cayman' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Chihuahua', label: 'America/Chihuahua' },
  { value: 'America/Ciudad_Juarez', label: 'America/Ciudad_Juarez' },
  { value: 'America/Costa_Rica', label: 'America/Costa_Rica' },
  { value: 'America/Creston', label: 'America/Creston' },
  { value: 'America/Cuiaba', label: 'America/Cuiaba' },
  { value: 'America/Curacao', label: 'America/Curacao' },
  { value: 'America/Danmarkshavn', label: 'America/Danmarkshavn' },
  { value: 'America/Dawson', label: 'America/Dawson' },
  { value: 'America/Dawson_Creek', label: 'America/Dawson_Creek' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Detroit', label: 'America/Detroit' },
  { value: 'America/Dominica', label: 'America/Dominica' },
  { value: 'America/Edmonton', label: 'America/Edmonton' },
  { value: 'America/Eirunepe', label: 'America/Eirunepe' },
  { value: 'America/El_Salvador', label: 'America/El_Salvador' },
  { value: 'America/Fort_Nelson', label: 'America/Fort_Nelson' },
  { value: 'America/Fortaleza', label: 'America/Fortaleza' },
  { value: 'America/Glace_Bay', label: 'America/Glace_Bay' },
  { value: 'America/Goose_Bay', label: 'America/Goose_Bay' },
  { value: 'America/Grand_Turk', label: 'America/Grand_Turk' },
  { value: 'America/Grenada', label: 'America/Grenada' },
  { value: 'America/Guadeloupe', label: 'America/Guadeloupe' },
  { value: 'America/Guatemala', label: 'America/Guatemala' },
  { value: 'America/Guayaquil', label: 'America/Guayaquil' },
  { value: 'America/Guyana', label: 'America/Guyana' },
  { value: 'America/Halifax', label: 'America/Halifax' },
  { value: 'America/Havana', label: 'America/Havana' },
  { value: 'America/Hermosillo', label: 'America/Hermosillo' },
  { value: 'America/Indiana/Indianapolis', label: 'America/Indiana/Indianapolis' },
  { value: 'America/Indiana/Knox', label: 'America/Indiana/Knox' },
  { value: 'America/Indiana/Marengo', label: 'America/Indiana/Marengo' },
  { value: 'America/Indiana/Petersburg', label: 'America/Indiana/Petersburg' },
  { value: 'America/Indiana/Tell_City', label: 'America/Indiana/Tell_City' },
  { value: 'America/Indiana/Vevay', label: 'America/Indiana/Vevay' },
  { value: 'America/Indiana/Vincennes', label: 'America/Indiana/Vincennes' },
  { value: 'America/Indiana/Winamac', label: 'America/Indiana/Winamac' },
  { value: 'America/Inuvik', label: 'America/Inuvik' },
  { value: 'America/Iqaluit', label: 'America/Iqaluit' },
  { value: 'America/Jamaica', label: 'America/Jamaica' },
  { value: 'America/Juneau', label: 'America/Juneau' },
  { value: 'America/Kentucky/Louisville', label: 'America/Kentucky/Louisville' },
  { value: 'America/Kentucky/Monticello', label: 'America/Kentucky/Monticello' },
  { value: 'America/Kralendijk', label: 'America/Kralendijk' },
  { value: 'America/La_Paz', label: 'America/La_Paz' },
  { value: 'America/Lima', label: 'America/Lima' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'America/Lower_Princes', label: 'America/Lower_Princes' },
  { value: 'America/Maceio', label: 'America/Maceio' },
  { value: 'America/Managua', label: 'America/Managua' },
  { value: 'America/Manaus', label: 'America/Manaus' },
  { value: 'America/Marigot', label: 'America/Marigot' },
  { value: 'America/Martinique', label: 'America/Martinique' },
  { value: 'America/Matamoros', label: 'America/Matamoros' },
  { value: 'America/Mazatlan', label: 'America/Mazatlan' },
  { value: 'America/Menominee', label: 'America/Menominee' },
  { value: 'America/Merida', label: 'America/Merida' },
  { value: 'America/Metlakatla', label: 'America/Metlakatla' },
  { value: 'America/Mexico_City', label: 'America/Mexico_City' },
  { value: 'America/Miquelon', label: 'America/Miquelon' },
  { value: 'America/Moncton', label: 'America/Moncton' },
  { value: 'America/Monterrey', label: 'America/Monterrey' },
  { value: 'America/Montevideo', label: 'America/Montevideo' },
  { value: 'America/Montserrat', label: 'America/Montserrat' },
  { value: 'America/Nassau', label: 'America/Nassau' },
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Nome', label: 'America/Nome' },
  { value: 'America/Noronha', label: 'America/Noronha' },
  { value: 'America/North_Dakota/Beulah', label: 'America/North_Dakota/Beulah' },
  { value: 'America/North_Dakota/Center', label: 'America/North_Dakota/Center' },
  { value: 'America/North_Dakota/New_Salem', label: 'America/North_Dakota/New_Salem' },
  { value: 'America/Nuuk', label: 'America/Nuuk' },
  { value: 'America/Ojinaga', label: 'America/Ojinaga' },
  { value: 'America/Panama', label: 'America/Panama' },
  { value: 'America/Paramaribo', label: 'America/Paramaribo' },
  { value: 'America/Phoenix', label: 'America/Phoenix' },
  { value: 'America/Port_of_Spain', label: 'America/Port_of_Spain' },
  { value: 'America/Port-au-Prince', label: 'America/Port-au-Prince' },
  { value: 'America/Porto_Velho', label: 'America/Porto_Velho' },
  { value: 'America/Puerto_Rico', label: 'America/Puerto_Rico' },
  { value: 'America/Punta_Arenas', label: 'America/Punta_Arenas' },
  { value: 'America/Rankin_Inlet', label: 'America/Rankin_Inlet' },
  { value: 'America/Recife', label: 'America/Recife' },
  { value: 'America/Regina', label: 'America/Regina' },
  { value: 'America/Resolute', label: 'America/Resolute' },
  { value: 'America/Rio_Branco', label: 'America/Rio_Branco' },
  { value: 'America/Santarem', label: 'America/Santarem' },
  { value: 'America/Santiago', label: 'America/Santiago' },
  { value: 'America/Santo_Domingo', label: 'America/Santo_Domingo' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo' },
  { value: 'America/Scoresbysund', label: 'America/Scoresbysund' },
  { value: 'America/Sitka', label: 'America/Sitka' },
  { value: 'America/St_Barthelemy', label: 'America/St_Barthelemy' },
  { value: 'America/St_Johns', label: 'America/St_Johns' },
  { value: 'America/St_Kitts', label: 'America/St_Kitts' },
  { value: 'America/St_Lucia', label: 'America/St_Lucia' },
  { value: 'America/St_Thomas', label: 'America/St_Thomas' },
  { value: 'America/St_Vincent', label: 'America/St_Vincent' },
  { value: 'America/Swift_Current', label: 'America/Swift_Current' },
  { value: 'America/Tegucigalpa', label: 'America/Tegucigalpa' },
  { value: 'America/Thule', label: 'America/Thule' },
  { value: 'America/Tijuana', label: 'America/Tijuana' },
  { value: 'America/Toronto', label: 'America/Toronto' },
  { value: 'America/Tortola', label: 'America/Tortola' },
  { value: 'America/Vancouver', label: 'America/Vancouver' },
  { value: 'America/Whitehorse', label: 'America/Whitehorse' },
  { value: 'America/Winnipeg', label: 'America/Winnipeg' },
  { value: 'America/Yakutat', label: 'America/Yakutat' },
  { value: 'Asia/Aden', label: 'Asia/Aden' },
  { value: 'Asia/Almaty', label: 'Asia/Almaty' },
  { value: 'Asia/Amman', label: 'Asia/Amman' },
  { value: 'Asia/Anadyr', label: 'Asia/Anadyr' },
  { value: 'Asia/Aqtau', label: 'Asia/Aqtau' },
  { value: 'Asia/Aqtobe', label: 'Asia/Aqtobe' },
  { value: 'Asia/Ashgabat', label: 'Asia/Ashgabat' },
  { value: 'Asia/Atyrau', label: 'Asia/Atyrau' },
  { value: 'Asia/Baghdad', label: 'Asia/Baghdad' },
  { value: 'Asia/Bahrain', label: 'Asia/Bahrain' },
  { value: 'Asia/Baku', label: 'Asia/Baku' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok' },
  { value: 'Asia/Barnaul', label: 'Asia/Barnaul' },
  { value: 'Asia/Beirut', label: 'Asia/Beirut' },
  { value: 'Asia/Bishkek', label: 'Asia/Bishkek' },
  { value: 'Asia/Brunei', label: 'Asia/Brunei' },
  { value: 'Asia/Chita', label: 'Asia/Chita' },
  { value: 'Asia/Choibalsan', label: 'Asia/Choibalsan' },
  { value: 'Asia/Colombo', label: 'Asia/Colombo' },
  { value: 'Asia/Damascus', label: 'Asia/Damascus' },
  { value: 'Asia/Dhaka', label: 'Asia/Dhaka' },
  { value: 'Asia/Dili', label: 'Asia/Dili' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai' },
  { value: 'Asia/Dushanbe', label: 'Asia/Dushanbe' },
  { value: 'Asia/Famagusta', label: 'Asia/Famagusta' },
  { value: 'Asia/Gaza', label: 'Asia/Gaza' },
  { value: 'Asia/Hebron', label: 'Asia/Hebron' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho_Chi_Minh' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong' },
  { value: 'Asia/Hovd', label: 'Asia/Hovd' },
  { value: 'Asia/Irkutsk', label: 'Asia/Irkutsk' },
  { value: 'Asia/Jakarta', label: 'Asia/Jakarta' },
  { value: 'Asia/Jayapura', label: 'Asia/Jayapura' },
  { value: 'Asia/Jerusalem', label: 'Asia/Jerusalem' },
  { value: 'Asia/Kabul', label: 'Asia/Kabul' },
  { value: 'Asia/Kamchatka', label: 'Asia/Kamchatka' },
  { value: 'Asia/Karachi', label: 'Asia/Karachi' },
  { value: 'Asia/Kathmandu', label: 'Asia/Kathmandu' },
  { value: 'Asia/Khandyga', label: 'Asia/Khandyga' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata' },
  { value: 'Asia/Krasnoyarsk', label: 'Asia/Krasnoyarsk' },
  { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala_Lumpur' },
  { value: 'Asia/Kuching', label: 'Asia/Kuching' },
  { value: 'Asia/Kuwait', label: 'Asia/Kuwait' },
  { value: 'Asia/Macau', label: 'Asia/Macau' },
  { value: 'Asia/Magadan', label: 'Asia/Magadan' },
  { value: 'Asia/Makassar', label: 'Asia/Makassar' },
  { value: 'Asia/Manila', label: 'Asia/Manila' },
  { value: 'Asia/Muscat', label: 'Asia/Muscat' },
  { value: 'Asia/Nicosia', label: 'Asia/Nicosia' },
  { value: 'Asia/Novokuznetsk', label: 'Asia/Novokuznetsk' },
  { value: 'Asia/Novosibirsk', label: 'Asia/Novosibirsk' },
  { value: 'Asia/Omsk', label: 'Asia/Omsk' },
  { value: 'Asia/Oral', label: 'Asia/Oral' },
  { value: 'Asia/Phnom_Penh', label: 'Asia/Phnom_Penh' },
  { value: 'Asia/Pontianak', label: 'Asia/Pontianak' },
  { value: 'Asia/Pyongyang', label: 'Asia/Pyongyang' },
  { value: 'Asia/Qatar', label: 'Asia/Qatar' },
  { value: 'Asia/Qostanay', label: 'Asia/Qostanay' },
  { value: 'Asia/Qyzylorda', label: 'Asia/Qyzylorda' },
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh' },
  { value: 'Asia/Sakhalin', label: 'Asia/Sakhalin' },
  { value: 'Asia/Samarkand', label: 'Asia/Samarkand' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore' },
  { value: 'Asia/Srednekolymsk', label: 'Asia/Srednekolymsk' },
  { value: 'Asia/Taipei', label: 'Asia/Taipei' },
  { value: 'Asia/Tashkent', label: 'Asia/Tashkent' },
  { value: 'Asia/Tbilisi', label: 'Asia/Tbilisi' },
  { value: 'Asia/Tehran', label: 'Asia/Tehran' },
  { value: 'Asia/Thimphu', label: 'Asia/Thimphu' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
  { value: 'Asia/Tomsk', label: 'Asia/Tomsk' },
  { value: 'Asia/Ulaanbaatar', label: 'Asia/Ulaanbaatar' },
  { value: 'Asia/Urumqi', label: 'Asia/Urumqi' },
  { value: 'Asia/Ust-Nera', label: 'Asia/Ust-Nera' },
  { value: 'Asia/Vientiane', label: 'Asia/Vientiane' },
  { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok' },
  { value: 'Asia/Yakutsk', label: 'Asia/Yakutsk' },
  { value: 'Asia/Yangon', label: 'Asia/Yangon' },
  { value: 'Asia/Yekaterinburg', label: 'Asia/Yekaterinburg' },
  { value: 'Asia/Yerevan', label: 'Asia/Yerevan' },
  { value: 'Atlantic/Azores', label: 'Atlantic/Azores' },
  { value: 'Atlantic/Bermuda', label: 'Atlantic/Bermuda' },
  { value: 'Atlantic/Canary', label: 'Atlantic/Canary' },
  { value: 'Atlantic/Cape_Verde', label: 'Atlantic/Cape_Verde' },
  { value: 'Atlantic/Faroe', label: 'Atlantic/Faroe' },
  { value: 'Atlantic/Madeira', label: 'Atlantic/Madeira' },
  { value: 'Atlantic/Reykjavik', label: 'Atlantic/Reykjavik' },
  { value: 'Atlantic/South_Georgia', label: 'Atlantic/South_Georgia' },
  { value: 'Atlantic/St_Helena', label: 'Atlantic/St_Helena' },
  { value: 'Atlantic/Stanley', label: 'Atlantic/Stanley' },
  { value: 'Antarctica/Casey', label: 'Antarctica/Casey' },
  { value: 'Antarctica/Davis', label: 'Antarctica/Davis' },
  { value: 'Antarctica/DumontDUrville', label: 'Antarctica/DumontDUrville' },
  { value: 'Antarctica/Macquarie', label: 'Antarctica/Macquarie' },
  { value: 'Antarctica/Mawson', label: 'Antarctica/Mawson' },
  { value: 'Antarctica/McMurdo', label: 'Antarctica/McMurdo' },
  { value: 'Antarctica/Palmer', label: 'Antarctica/Palmer' },
  { value: 'Antarctica/Rothera', label: 'Antarctica/Rothera' },
  { value: 'Antarctica/Syowa', label: 'Antarctica/Syowa' },
  { value: 'Antarctica/Troll', label: 'Antarctica/Troll' },
  { value: 'Antarctica/Vostok', label: 'Antarctica/Vostok' },
  { value: 'Arctic/Longyearbyen', label: 'Arctic/Longyearbyen' },
];

// Form validation schema
const settingsSchema = yup.object({
  full_name: yup.string().required('Full name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  username: yup.string().required('Username is required'),
  umbrella_user_id: yup.string().nullable(),
  timezone: yup.string().required('Timezone is required'),
  customer_timezone: yup.string().required('Customer timezone is required'),
  email_notifications: yup.boolean(),
  synthflow_api_token: yup.string().nullable(),
});

// Email credentials validation schema
const emailCredentialsSchema = yup.object({
  smtp_tls: yup.boolean().required(),
  smtp_port: yup.number().oneOf([25, 465, 587, 2525], 'SMTP port must be one of: 25, 465, 587, 2525').required('SMTP port is required'),
  smtp_host: yup.string().min(1, 'SMTP host is required').max(255, 'SMTP host too long').required('SMTP host is required'),
  smtp_user: yup.string().min(1, 'SMTP user is required').max(255, 'SMTP user too long').required('SMTP user is required'),
  smtp_password: yup.string().min(1, 'SMTP password is required').required('SMTP password is required'),
  from_email_address: yup.string().email('Invalid email address').required('From email address is required'),
  emails_from_name: yup.string().min(1, 'From name is required').max(255, 'From name too long').required('From name is required'),
  closing_salutation: yup.string().max(255, 'Closing salutation too long').nullable(),
  closing_name: yup.string().max(255, 'Closing name too long').nullable(),
});

type SettingsFormData = yup.InferType<typeof settingsSchema>;
type EmailCredentialsFormData = yup.InferType<typeof emailCredentialsSchema>;

export const SettingsPage: React.FC = () => {
  const [showSynthflowToken, setShowSynthflowToken] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Address Book state
  const [newEmailInput, setNewEmailInput] = useState('');
  const [newEmailLabel, setNewEmailLabel] = useState('');
  const [editingEmail, setEditingEmail] = useState<EmailAddressDropdown | null>(null);

  // API hooks
  const { data: currentUser, isLoading: userLoading } = useGetCurrentUserQuery();
  const [updateUser, { isLoading: updateLoading }] = useUpdateUserMutation();
  
  // Email credentials API hooks
  const { data: emailCredentials, isLoading: emailCredentialsLoading } = useGetEmailCredentialsQuery();
  const [createEmailCredentials, { isLoading: createEmailCredentialsLoading }] = useCreateEmailCredentialsMutation();
  const [updateEmailCredentials, { isLoading: updateEmailCredentialsLoading }] = useUpdateEmailCredentialsMutation();
  const [deleteEmailCredentials, { isLoading: deleteEmailCredentialsLoading }] = useDeleteEmailCredentialsMutation();

  // Address Book API hooks
  const { data: emailAddresses = [], isLoading: emailAddressesLoading } = useGetEmailAddressesQuery();
  const [createEmailAddress, { isLoading: createEmailAddressLoading }] = useCreateEmailAddressMutation();
  const [updateEmailAddress, { isLoading: updateEmailAddressLoading }] = useUpdateEmailAddressMutation();
  const [deleteEmailAddress, { isLoading: deleteEmailAddressLoading }] = useDeleteEmailAddressMutation();

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<SettingsFormData>({
    resolver: yupResolver(settingsSchema),
    defaultValues: {
      full_name: '',
      email: '',
      username: '',
      umbrella_user_id: '',
      timezone: 'US/Eastern',
      customer_timezone: 'US/Eastern',
      email_notifications: true,
      synthflow_api_token: '',
    }
  });

  // Email credentials form setup
  const {
    control: emailControl,
    handleSubmit: handleEmailSubmit,
    reset: resetEmailForm,
    formState: { errors: emailErrors, isDirty: isEmailDirty }
  } = useForm<EmailCredentialsFormData>({
    resolver: yupResolver(emailCredentialsSchema),
    defaultValues: {
      smtp_tls: true,
      smtp_port: 587,
      smtp_host: '',
      smtp_user: '',
      smtp_password: '',
      from_email_address: '',
      emails_from_name: '',
      closing_salutation: '',
      closing_name: '',
    }
  });

  // Load user data when available
  useEffect(() => {
    if (currentUser) {
        reset({
          full_name: currentUser.full_name || '',
          email: currentUser.email || '',
          username: currentUser.username || '',
          umbrella_user_id: currentUser.umbrella_user_id || '',
          timezone: currentUser.timezone || 'US/Eastern',
          customer_timezone: currentUser.customer_timezone || 'US/Eastern',
          email_notifications: currentUser.email_notifications ?? true,
          synthflow_api_token: currentUser.synthflow_api_token || '',
        });
    }
  }, [currentUser, reset]);

  // Load email credentials when available
  useEffect(() => {
    if (emailCredentials) {
      resetEmailForm({
        smtp_tls: emailCredentials.smtp_tls,
        smtp_port: emailCredentials.smtp_port,
        smtp_host: emailCredentials.smtp_host,
        smtp_user: emailCredentials.smtp_user,
        smtp_password: emailCredentials.smtp_password,
        from_email_address: emailCredentials.from_email_address,
        emails_from_name: emailCredentials.emails_from_name,
        closing_salutation: emailCredentials.closing_salutation || '',
        closing_name: emailCredentials.closing_name || '',
      });
    }
  }, [emailCredentials, resetEmailForm]);

  const onSubmit = async (data: SettingsFormData) => {
    try {
      // Convert null values to undefined for API compatibility
      const apiData = {
        ...data,
        synthflow_api_token: data.synthflow_api_token || undefined,
      };
      await updateUser(apiData).unwrap();
      setShowSuccess(true);
    } catch (error: any) {
      setErrorMessage(error?.data?.detail || 'Failed to update settings');
      setShowError(true);
    }
  };

  const onEmailCredentialsSubmit = async (data: EmailCredentialsFormData) => {
    try {
      console.log('Submitting email credentials:', data);
      
      // Convert null values to undefined for API compatibility
      const apiData = {
        ...data,
        closing_salutation: data.closing_salutation || undefined,
        closing_name: data.closing_name || undefined,
      };
      
      if (emailCredentials) {
        // Update existing credentials
        console.log('Updating existing credentials...');
        await updateEmailCredentials(apiData).unwrap();
      } else {
        // Create new credentials
        console.log('Creating new credentials...');
        await createEmailCredentials(apiData).unwrap();
      }
      console.log('Email credentials saved successfully');
      setShowSuccess(true);
    } catch (error: any) {
      console.error('Email credentials submission error:', error);
      setErrorMessage(error?.data?.detail || 'Failed to save email credentials');
      setShowError(true);
    }
  };

  const onDeleteEmailCredentials = async () => {
    try {
      await deleteEmailCredentials().unwrap();
      resetEmailForm();
      setShowSuccess(true);
    } catch (error: any) {
      setErrorMessage(error?.data?.detail || 'Failed to delete email credentials');
      setShowError(true);
    }
  };

  // Address Book functions
  const handleAddEmailAddress = async () => {
    if (!newEmailInput.trim()) return;
    
    try {
      await createEmailAddress({
        email: newEmailInput.trim(),
        label: newEmailLabel.trim() || undefined
      }).unwrap();
      
      setNewEmailInput('');
      setNewEmailLabel('');
      setShowSuccess(true);
    } catch (error: any) {
      setErrorMessage(error?.data?.detail || 'Failed to add email address');
      setShowError(true);
    }
  };

  const handleUpdateEmailAddress = async (id: number, email: string, label?: string) => {
    try {
      await updateEmailAddress({
        id,
        data: {
          email: email.trim(),
          label: label?.trim() || undefined
        }
      }).unwrap();
      
      setEditingEmail(null);
      setShowSuccess(true);
    } catch (error: any) {
      setErrorMessage(error?.data?.detail || 'Failed to update email address');
      setShowError(true);
    }
  };

  const handleDeleteEmailAddress = async (id: number) => {
    try {
      await deleteEmailAddress(id).unwrap();
      setShowSuccess(true);
    } catch (error: any) {
      setErrorMessage(error?.data?.detail || 'Failed to delete email address');
      setShowError(true);
    }
  };

  if (userLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Manage your account settings and API configurations
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
        {/* Profile Settings Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Person color="primary" />
              <Typography variant="h6">Profile Settings</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="full_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Full Name"
                      variant="outlined"
                      error={!!errors.full_name}
                      helperText={errors.full_name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email"
                      type="email"
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Username"
                      variant="outlined"
                      error={!!errors.username}
                      helperText={errors.username?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="umbrella_user_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Umbrella User ID"
                      variant="outlined"
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Your unique 5-digit Umbrella identifier"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="email_notifications"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Email Notifications"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Address Book Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Contacts color="primary" />
              <Typography variant="h6">Address Book</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage your email addresses for call summary notifications. These addresses can be assigned to agents as TO, CC, or BCC recipients.
              </Typography>
              
              {/* Add New Email Address */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Add New Email Address
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      value={newEmailInput}
                      onChange={(e) => setNewEmailInput(e.target.value)}
                      placeholder="example@domain.com"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Label (Optional)"
                      value={newEmailLabel}
                      onChange={(e) => setNewEmailLabel(e.target.value)}
                      placeholder="John Doe"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleAddEmailAddress}
                      disabled={!newEmailInput.trim() || createEmailAddressLoading}
                      fullWidth
                    >
                      {createEmailAddressLoading ? 'Adding...' : 'Add Email'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Email Addresses List */}
              <Typography variant="subtitle1" gutterBottom>
                Saved Email Addresses
              </Typography>
              {emailAddressesLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : emailAddresses.length === 0 ? (
                <Alert severity="info">
                  No email addresses saved yet. Add your first email address above.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {emailAddresses.map((emailAddr) => (
                    <Grid item xs={12} sm={6} md={4} key={emailAddr.id}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight="medium">
                                {emailAddr.label || 'No Label'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {emailAddr.email}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => setEditingEmail(emailAddr)}
                                disabled={updateEmailAddressLoading || deleteEmailAddressLoading}
                              >
                                <Save />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteEmailAddress(emailAddr.id)}
                                disabled={updateEmailAddressLoading || deleteEmailAddressLoading}
                              >
                                {deleteEmailAddressLoading ? <CircularProgress size={16} /> : <Delete />}
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Timezone Settings Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Schedule color="primary" />
              <Typography variant="h6">Timezone Settings</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      fullWidth
                      options={timezoneOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      value={timezoneOptions.find(option => option.value === field.value) || null}
                      onChange={(_, newValue) => field.onChange(newValue?.value || 'US/Eastern')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Display Timezone"
                          variant="outlined"
                          error={!!errors.timezone}
                          helperText="Timezone for displaying dates and times in the UI"
                        />
                      )}
                      filterOptions={(options, { inputValue }) => {
                        return options.filter(option =>
                          option.label.toLowerCase().includes(inputValue.toLowerCase())
                        );
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="customer_timezone"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      fullWidth
                      options={timezoneOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      value={timezoneOptions.find(option => option.value === field.value) || null}
                      onChange={(_, newValue) => field.onChange(newValue?.value || 'US/Eastern')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer Timezone"
                          variant="outlined"
                          error={!!errors.customer_timezone}
                          helperText="Default timezone for your customers (used for scheduling and reports)"
                        />
                      )}
                      filterOptions={(options, { inputValue }) => {
                        return options.filter(option =>
                          option.label.toLowerCase().includes(inputValue.toLowerCase())
                        );
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Display Timezone:</strong> Controls how dates and times are shown in the user interface.<br/>
                  <strong>Customer Timezone:</strong> Used for scheduling calls, sending reports, and other customer-facing operations.
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Synthflow Settings Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <SmartToy color="primary" />
              <Typography variant="h6">Synthflow Configuration</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="synthflow_api_token"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="API Key"
                      type={showSynthflowToken ? "text" : "password"}
                      variant="outlined"
                      placeholder="Enter your Synthflow API Key"
                      helperText="Your Synthflow API Key for automated voice calls"
                      error={!!errors.synthflow_api_token}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowSynthflowToken(!showSynthflowToken)}
                              edge="end"
                            >
                              {showSynthflowToken ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Outbound Email Credentials Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Public color="primary" />
              <Typography variant="h6">Outbound Email Credentials</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure SMTP settings for sending call summary emails. All fields are required.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtp_host"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="SMTP Host"
                        variant="outlined"
                        placeholder="smtp.gmail.com"
                        error={!!emailErrors.smtp_host}
                        helperText={emailErrors.smtp_host?.message || "SMTP server hostname"}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtp_port"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="SMTP Port"
                        type="number"
                        variant="outlined"
                        error={!!emailErrors.smtp_port}
                        helperText={emailErrors.smtp_port?.message || "Port: 25, 465, 587, or 2525"}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtp_user"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="SMTP Username"
                        variant="outlined"
                        placeholder="your-email@gmail.com"
                        error={!!emailErrors.smtp_user}
                        helperText={emailErrors.smtp_user?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtp_password"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="SMTP Password"
                        type={showEmailPassword ? "text" : "password"}
                        variant="outlined"
                        placeholder="Your app password"
                        error={!!emailErrors.smtp_password}
                        helperText={emailErrors.smtp_password?.message || "Use app password"}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowEmailPassword(!showEmailPassword)}
                                edge="end"
                              >
                                {showEmailPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="smtp_tls"
                    control={emailControl}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        }
                        label="Use TLS/SSL (recommended for most providers)"
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={onDeleteEmailCredentials}
                  disabled={!emailCredentials || deleteEmailCredentialsLoading}
                >
                  {deleteEmailCredentialsLoading ? 'Deleting...' : 'Delete Credentials'}
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleEmailSubmit(onEmailCredentialsSubmit)}
                  startIcon={createEmailCredentialsLoading || updateEmailCredentialsLoading ? <CircularProgress size={20} /> : <Save />}
                  disabled={createEmailCredentialsLoading || updateEmailCredentialsLoading || !isEmailDirty}
                >
                  {createEmailCredentialsLoading || updateEmailCredentialsLoading 
                    ? 'Saving...' 
                    : emailCredentials ? 'Update Credentials' : 'Save Credentials'
                  }
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Outbound Email Configs Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <SettingsIcon color="primary" />
              <Typography variant="h6">Outbound Email Configs</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure email display settings and closing information for call summary emails.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="from_email_address"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="From Email Address"
                        type="email"
                        variant="outlined"
                        placeholder="noreply@yourdomain.com"
                        error={!!emailErrors.from_email_address}
                        helperText={emailErrors.from_email_address?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="emails_from_name"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="From Name"
                        variant="outlined"
                        placeholder="Your Company Name"
                        error={!!emailErrors.emails_from_name}
                        helperText={emailErrors.emails_from_name?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="closing_salutation"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Closing Salutation"
                        variant="outlined"
                        placeholder="Best regards"
                        error={!!emailErrors.closing_salutation}
                        helperText={emailErrors.closing_salutation?.message || "How to close the email (e.g., 'Best regards', 'Sincerely')"}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="closing_name"
                    control={emailControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Closing Name"
                        variant="outlined"
                        placeholder="Your Company Name"
                        error={!!emailErrors.closing_name}
                        helperText={emailErrors.closing_name?.message || "Name to sign the email with"}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleEmailSubmit(onEmailCredentialsSubmit)}
                  startIcon={createEmailCredentialsLoading || updateEmailCredentialsLoading ? <CircularProgress size={20} /> : <Save />}
                  disabled={createEmailCredentialsLoading || updateEmailCredentialsLoading || !isEmailDirty}
                >
                  {createEmailCredentialsLoading || updateEmailCredentialsLoading 
                    ? 'Saving...' 
                    : emailCredentials ? 'Update Email Configs' : 'Save Email Configs'
                  }
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>


        {/* Save Button */}
        <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={updateLoading ? <CircularProgress size={20} /> : <Save />}
            size="large"
            disabled={updateLoading || !isDirty}
            sx={{ minWidth: 150 }}
          >
            {updateLoading ? 'Saving...' : 'Save All Settings'}
          </Button>
        </Box>
      </Box>

      {/* Success Notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
